import React, {
  ComponentProps,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridRowsProp,
  GridSelectionModel,
  itIT,
} from "@mui/x-data-grid";
import { useForm } from "react-hook-form";
import { DataContext } from "../Context/ContextWrapper";

/* Costanti & Tipi */
const COLORS = {
  navy: "#003267",
  white: "#FFFFFF",
  zebraEven: "#F8F9FC",
  zebraOdd: "#FFFFFF",
  border: "#CCCCCC",
  text: "#00000099",
};

const sectionTitleSx = { fontWeight: 700, color: COLORS.text, mb: 1 };

//Tipo per la gestione delle row della tabella
export type CombinedProceduresRow = {
  id: string;
  idMedPgm: string;
  aziendaPrenotazione: string;
  codice: string;
  descrizione: string;
  durataTot: number;
  composizioni: {
    specialita?: string;
    prestazione?: string;
    codSiss?: string;
    descrizioneSiss?: string;
    durata?: number;
  }[];
};

// Per gestire il modale
type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm?: (selected: CombinedProceduresRow[]) => void;
  rows?: CombinedProceduresRow[] | null;
  idPacada: string;
};

type FormValues = {
  tipologiaRaggruppabili: string | null;
  tipologiaRaggruppamento: string | null;
  slotOccupati: string | null;
};

/*  Styled DataGrid */
function StyledDataGrid(props: ComponentProps<typeof DataGrid>) {
  return (
    <DataGrid
      {...props}
      sx={{
        border: `1px solid ${COLORS.border}`,
        width: "100%",
        borderRadius: 2,
        display: "flex",
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: COLORS.navy,
          color: COLORS.white,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
        "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
          color: COLORS.white,
        },
        "& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-sortIcon": {
          color: COLORS.white,
        },
        "& .MuiDataGrid-columnSeparator": { display: "none" },
        "& .MuiDataGrid-row:nth-of-type(even)": {
          backgroundColor: COLORS.zebraEven,
        },
        "& .MuiDataGrid-row:nth-of-type(odd)": {
          backgroundColor: COLORS.zebraOdd,
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
          { outline: "none" },
        // Per evidenziare le righe quando sono selezionate
        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover":
          {
            backgroundColor: "#DFEAFF !important",
          },
        ...(props.sx || {}),
      }}
      headerHeight={36}
      rowHeight={40}
      hideFooter
      disableColumnFilter
      disableColumnSelector
      disableColumnMenu
      localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
    />
  );
}

/*  Creazione delle colonne */
const createColumns = (): GridColDef[] => [
  { field: "codice", headerName: "Codice", flex: 1, sortable: false },
  {
    field: "descrizione",
    headerName: "Descrizione",
    flex: 3.5,
    sortable: false,
  },
  { field: "durataTot", headerName: "Durata Tot.", flex: 0.7, sortable: false },
];

const createColumnsSelected = (): GridColDef[] => [
  { field: "specialita", headerName: "Specialità", flex: 1, sortable: false },
  {
    field: "prestazione",
    headerName: "Prestazione",
    flex: 1,
    sortable: false,
  },
  { field: "codSiss", headerName: "Cod. SISS", flex: 1, sortable: false },
  {
    field: "descrizioneSiss",
    headerName: "Descrizione SISS",
    flex: 3,
    sortable: false,
  },
  {
    field: "durata",
    headerName: "Durata",
    flex: 0.9,
    sortable: false,
  },
];

/* Inizio del componente */
const CombinedProcedures: FC<Props> = ({ open, onClose, onConfirm, rows, idPacada }) => {
  const context = useContext(DataContext);

  const [data, setData] = useState<GridRowsProp<CombinedProceduresRow>>(
    rows ?? []
  );

  useEffect(() => {
    if (rows && Array.isArray(rows)) {
      setData(rows);
    } else {
      setData([]);
    }
  }, [rows]);

  useEffect(() => {
    console.log("Data combined", context?.combinedProcedurePayload);
  },[context?.combinedProcedurePayload])

  const [selectionMaster, setSelectionMaster] = useState<GridSelectionModel>(
    []
  );
  const [selectionDetail, setSelectionDetail] = useState<GridSelectionModel>(
    []
  );
  const [selectedRows, setSelectedRows] = useState<
    CombinedProceduresRow["composizioni"]
  >([]);

  const cols = useMemo(createColumns, []);
  const colsSelected = useMemo(createColumnsSelected, []);

  const handleSelectionChangeMaster = (newSelection: GridSelectionModel) => {
    const singleSelection = newSelection.slice(-1);

    setSelectionMaster(singleSelection);

    const selectedId = singleSelection[0];
    const selected = data.find((d) => String(d.id) === String(selectedId));

    setSelectedRows(selected ? selected.composizioni : []);
    setSelectionDetail([]);
  };

  const handleSelectionChangeDetail = (newSelection: GridSelectionModel) => {
    setSelectionDetail(newSelection);
  };

  const handleConfirm = useCallback(() => {
    try {
      if (selectionMaster.length === 0) {
      console.warn("Nessuna combinata selezionata");
      return;
    }

    const selectedId = selectionMaster[0];
    const selected = data.find((d) => String(d.id) === String(selectedId));

    if (!selected) {
      console.warn("Combinata non trovata");
      return;
    }

    context?.setCombinedProcedurePayload({
      idPacada: idPacada ?? "",
      aziendaPrenotazione: selected.aziendaPrenotazione ?? "",
      codIstcom: selected.codice ?? "",
      idMedpgm: selected.idMedPgm ?? ""
    });

    console.log("Combinata selezionata:", selected);
    console.log("Composizioni:", selected.composizioni);

    onConfirm?.([selected]);
    } catch (error) {
      console.error({error});
    }
  }, [data, selectionMaster, onConfirm, onClose]);

  return (
    <Modal
      open={open}
      onClose={(_, r) => (r === "backdropClick" ? null : onClose())}
      disableEscapeKeyDown
      keepMounted
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.2)" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(92vw, 980px)",
          maxHeight: "88vh",
          outline: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.white,
            overflow: "auto",
            maxHeight: "88vh",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: "Rubik", fontWeight: 500, color: "black", mb: 1 }}
          >
            Prestazioni combinate
          </Typography>

          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Istanze combinate
          </Typography>
          <StyledDataGrid
            editMode="cell"
            rows={data}
            columns={cols}
            checkboxSelection
            selectionModel={selectionMaster}
            onSelectionModelChange={handleSelectionChangeMaster}
            disableSelectionOnClick
            sx={{
              height: 200,
              mb: 2.5,
              '& .MuiDataGrid-columnHeaders [data-field="__pen__"] .MuiDataGrid-sortIcon':
                { display: "none" },
            }}
          />

          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Dettaglio composizione
          </Typography>
          <StyledDataGrid
            rows={selectedRows.map((r, idx) => ({ id: idx.toString(), ...r }))}
            columns={colsSelected}
            disableSelectionOnClick
            selectionModel={selectionDetail}
            onSelectionModelChange={handleSelectionChangeDetail}
            experimentalFeatures={{ newEditingApi: true }}
            sx={{
              height: 200,
              mb: 2,
              '& .MuiDataGrid-columnHeaders [data-field="__pen__"] .MuiDataGrid-sortIcon':
                { display: "none" },
            }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Button variant="outlinedError" onClick={onClose}>
                Uscita
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={selectionMaster?.length < 1}
                >
                  Conferma
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  );
};

export default CombinedProcedures;
